import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import React, { useMemo, useState } from 'react';
import ReactPlayer from 'react-player';
import { useOrientation, useWindowSize } from 'react-use';

import { useDomainConfiguration } from '@/components/DomainConfigurationProvider';

import {
    PageLocale,
    WatchMoreVideo
} from '@cineamo/api/models/premium-movie-landing-page-configuration/PremiumMovieLandingPageConfigurationDto';

import { VIMEO_THUMBNAIL_IMAGE_DEFAULT, VIMEO_VIDEO_URL } from '@cineamo/api/constants/vimeo-constants';
import { YOUTUBE_EMBED_VIDEO_URL, YOUTUBE_THUMBNAIL_IMAGE_MAX_RES } from '@cineamo/api/constants/youtube-constants';

import { useIsMobile } from '@cineamo/ui/helpers/screen-type';

import Badge from '@cineamo/ui/badge/Badge';
import FullScreenModal from '@cineamo/ui/src/modals/FullScreenModal';

import MovieTrailerPlayerButton from '@cineamo/composables/src/movie/MovieTrailerPlayerButton';
import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import SectionHeader from './SectionHeader';

/**
 * Placeholder component for "Watch More" videos section.
 * This is specific to movie-landingpage and shows additional video content.
 * Implementation will be added later based on requirements.
 */
const WatchMoreSection: React.FC<{
    className?: string;
}> = ({ className }) => {
    const t = useTranslations();
    const { track } = useAnalytics();
    const { domainConfiguration } = useDomainConfiguration();
    const locale = useLocale();
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [selectedVideo, setSelectedVideo] = useState<WatchMoreVideo | null>(null);

    const videos = domainConfiguration.watchMoreVideos?.[locale as PageLocale] ?? [];
    const { width, height } = useWindowSize();
    const orientation = useOrientation();
    const isMobile = useIsMobile('lg');

    const playerSize = useMemo(() => {
        const ratio = 540 / 960;
        // 100 pixels added for player controls
        const playerHeightOnMobile = width * ratio + 100;
        return {
            width: isMobile ? width : 960,
            // 100 px
            height: isMobile ? (orientation.type === 'landscape-primary' ? height : playerHeightOnMobile) : 540
        };
    }, [orientation.type, isMobile, width, height]);

    if (!videos?.length) {
        return null;
    }
    return (
        <div className={className}>
            {modalIsOpen && (
                <FullScreenModal
                    modalIsOpen={modalIsOpen}
                    setModalIsOpen={setModalIsOpen}
                    onClose={() => setModalIsOpen(false)}>
                    <ReactPlayer
                        url={
                            selectedVideo?.site === 'YouTube'
                                ? YOUTUBE_EMBED_VIDEO_URL(selectedVideo?.key)
                                : VIMEO_VIDEO_URL(selectedVideo?.key)
                        }
                        playing={modalIsOpen}
                        onEnded={() => setModalIsOpen(false)}
                        controls={true}
                        // ref={headerVideoPlayerRef}
                        width={playerSize.width}
                        height={playerSize.height}
                    />
                </FullScreenModal>
            )}
            <SectionHeader title={t('movie.watch-more')} />
            <div className="grid grid-cols-2 gap-16 lg:grid-cols-3 lg:gap-x-36 lg:gap-y-24">
                {videos.map((video) => (
                    <div key={video.key}>
                        <div className="relative rounded-8 overflow-hidden">
                            <Image
                                src={
                                    video.site === 'YouTube'
                                        ? YOUTUBE_THUMBNAIL_IMAGE_MAX_RES(video.key)
                                        : VIMEO_THUMBNAIL_IMAGE_DEFAULT(video.key)
                                }
                                alt={video.name}
                                className="w-full h-full object-cover"
                                width={340}
                                height={189}
                            />
                            {video.duration && (
                                <Badge
                                    name={video.duration}
                                    className="absolute bottom-8 right-8 text-body-12"
                                    bgColor="bg-[rgba(17,17,17,0.7)]"
                                    textColor="text-white"
                                />
                            )}
                            <MovieTrailerPlayerButton
                                className="w-[72px] h-[72px]"
                                aria-label={`${t('trailer.play')}: ${video.name}`}
                                onPlay={() => {
                                    setModalIsOpen(true);
                                    setSelectedVideo(video);
                                    track('Watch More Video Opened', {
                                        videoKey: video.key,
                                        videoName: video.name,
                                        videoSite: video.site
                                    });
                                }}
                            />
                        </div>
                        <div className="text-headline-16 my-8">{video.name}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default WatchMoreSection;
